import React, { FunctionComponent } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { Container } from "react-bootstrap";

import FirebaseService from "../../actions/firebase/FirebaseActions";
import ButtonRow from "../common/button/ButtonRow";
import { BackButton } from "../common/button/Buttons";
import { useAuthentication } from "../../hooks/useAuthentication";
import RegistrationEditor from "./RegistrationEditor";
import { useFormSchema } from "../../hooks/useFormSchema";
import { trackFormEvent, trackRegistrationEvent } from "../../helper/analytics";
import { trackPlausibleGoal, PlausibleGoals } from "../../helper/plausibleAnalytics";
import { useUserIdParam } from "../../hooks/useUserIdParam";

interface Props {
  isAuthenticated: boolean;
}

const EMPTY_FORM_DATA = {};

const CreateRegistrationEditor: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const userId = useUserIdParam();
  const [formSchema] = useFormSchema(userId);
  const navigate = useNavigate();
  useAuthentication(isAuthenticated);

  const saveRegistration = async (formData: unknown): Promise<void> => {
    trackFormEvent("completed", "create-registration-form");

    try {
      const registrationId = await FirebaseService.createRegistration(userId, formData);
      trackRegistrationEvent("created", {
        registration_id: registrationId,
        form_data_keys: Object.keys(formData as Record<string, any>),
        timestamp: new Date().toISOString()
      });
      trackPlausibleGoal(PlausibleGoals.REGISTRATION_CREATED);

      navigate(`/user/${userId}/registrations/${registrationId}`);
      toast.success("🎉 Die Änderungen wurden gespeichert");
    } catch (e) {
      console.log(e);
      trackRegistrationEvent("create_failed", {
        error: e instanceof Error ? e.message : "Unknown error",
        timestamp: new Date().toISOString()
      });
      trackFormEvent("error", "create-registration-form", {
        error: e instanceof Error ? e.message : "Save failed"
      });
      toast.error(
        "Es ist ein Fehler beim Speichern der Daten aufgetreten. Bitte laden Sie die Applikation erneut und versuchen Sie es noch einmal."
      );
    }
  };

  if (!formSchema) return <p>Daten werden geladen</p>;
  else {
    return (
      <Container className={"h-100 py-5"}>
        <Helmet>
          <title>Spielgruppe Online - Anmeldung bearbeiten</title>
        </Helmet>
        <Container>
          <ButtonRow>
            <BackButton to={`/user/${userId}/registrations`} />
          </ButtonRow>
          <h3>Anmeldung erstellen</h3>
          <div className="editor">
            <RegistrationEditor formSchema={formSchema} formData={EMPTY_FORM_DATA} onSubmit={saveRegistration} />
          </div>
        </Container>
      </Container>
    );
  }
};

export default CreateRegistrationEditor;
